import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnfoldLess, UnfoldMore } from '@mui/icons-material';
import { Box, ButtonBase, Chip, Divider, IconButton, List, ListItem, ListItemText, Paper, Tooltip, Typography } from '@mui/material';

import type {
  CoverageSelections,
  NormalizedModuleMap,
  PatientCoverageSummary,
  PatientSummaryWithCoverages,
  SelectionKey,
} from '../../types/merge-customization';

import type { DemographicSelections } from './DemographicFieldSelector';
import { DEMOGRAPHIC_FIELDS } from './DemographicFieldSelector';

const SLOT_LABELS: Record<number, string> = {
  1: 'Primary',
  2: 'Secondary',
  3: 'Tertiary',
};

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatCoveragePreview(coverage: PatientCoverageSummary): string {
  if (coverage.payer_name && coverage.plan_name) {
    return `${coverage.payer_name} — ${coverage.plan_name}`;
  }
  if (coverage.payer_name) return coverage.payer_name;
  if (coverage.plan_name) return coverage.plan_name;
  if (coverage.member_id) return coverage.member_id;
  return 'Coverage';
}

interface MergePreviewPanelProps {
  modules: NormalizedModuleMap;
  selections: Set<SelectionKey>;
  patients: PatientSummaryWithCoverages[];
  defaultPatientId: string;
  demographicSelections: DemographicSelections;
  coverageSelections: CoverageSelections;
  activeSection: string | null;
  onSectionChange: (section: string) => void;
}

export function MergePreviewPanel({
  modules,
  selections,
  patients,
  defaultPatientId,
  demographicSelections,
  coverageSelections,
  activeSection,
  onSectionChange,
}: MergePreviewPanelProps) {
  const [expandAll, setExpandAll] = useState(false);

  // Refs for scroll-into-view
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  const setSectionRef = useCallback((key: string, el: HTMLDivElement | null) => {
    sectionRefs.current[key] = el;
  }, []);

  // Auto-scroll to active section
  useEffect(() => {
    if (expandAll || !activeSection) return;
    const el = sectionRefs.current[activeSection];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeSection, expandAll]);

  // Resolve final demographic values
  const resolvedDemographics = useMemo(() => {
    const patientById = new Map(patients.map(p => [p.patient_id, p]));
    return DEMOGRAPHIC_FIELDS.map(field => {
      const sourceId = demographicSelections[field.patchKey] ?? defaultPatientId;
      const source = patientById.get(sourceId);
      const rawValue = source?.[field.summaryKey];
      const displayValue =
        rawValue != null && rawValue !== ''
          ? field.format
            ? field.format(String(rawValue))
            : String(rawValue)
          : null;
      return { label: field.label, value: displayValue };
    }).filter(d => d.value != null);
  }, [patients, defaultPatientId, demographicSelections]);

  // Build demographics summary line
  const demographicsSummary = useMemo(() => {
    const parts = resolvedDemographics.slice(0, 3).map(d => d.value);
    return parts.join(', ') || 'No demographics';
  }, [resolvedDemographics]);

  // Resolve final coverage values from selections (slot → coverageId)
  const resolvedCoverages = useMemo(() => {
    const coverageById = new Map<string, PatientCoverageSummary>();
    for (const patient of patients) {
      if (!patient.coverages) continue;
      for (const cov of patient.coverages) {
        coverageById.set(cov.patient_insurance_coverage_id, cov);
      }
    }

    const result: { label: string; value: string }[] = [];
    const sortedSlots = Object.entries(coverageSelections).sort(([a], [b]) => Number(a) - Number(b));

    for (const [slot, coverageId] of sortedSlots) {
      const coverage = coverageById.get(coverageId);
      if (!coverage) continue;

      const slotLabel = SLOT_LABELS[Number(slot)] ?? `Slot ${slot}`;
      result.push({ label: slotLabel, value: formatCoveragePreview(coverage) });
    }

    return result;
  }, [patients, coverageSelections]);

  // Build coverage summary line
  const coverageSummary = useMemo(() => {
    if (resolvedCoverages.length === 0) return 'No coverages';
    return resolvedCoverages.map(c => `${c.label}: ${c.value}`).join(', ');
  }, [resolvedCoverages]);

  const moduleEntries = Object.entries(modules)
    .map(([key, mod]) => {
      const selectedItems = mod.items.filter(i => selections.has(i.selectionKey));
      return { key, mod, selectedItems };
    })
    .filter(entry => entry.selectedItems.length > 0);

  const totalSelected = moduleEntries.reduce((sum, e) => sum + e.selectedItems.length, 0);

  const isSectionActive = useCallback(
    (section: string) => expandAll || activeSection === section,
    [expandAll, activeSection],
  );

  const handleToggleExpandAll = useCallback(() => {
    setExpandAll(prev => !prev);
  }, []);

  const hasDemographics = resolvedDemographics.length > 0;
  const hasCoverages = resolvedCoverages.length > 0;
  const hasModules = moduleEntries.length > 0;
  const hasContent = hasDemographics || hasCoverages || hasModules;

  return (
    <Paper
      ref={scrollContainerRef}
      variant="outlined"
      sx={{
        position: 'sticky',
        top: 24,
        maxHeight: 'calc(100vh - 180px)',
        overflow: 'auto',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'primary.50',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Merged Result Preview
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {totalSelected} item{totalSelected !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
        {hasContent && (
          <Tooltip title={expandAll ? 'Collapse sections' : 'Expand all sections'}>
            <IconButton
              size="small"
              onClick={handleToggleExpandAll}
              aria-label={expandAll ? 'Collapse sections' : 'Expand all sections'}
            >
              {expandAll ? <UnfoldLess fontSize="small" /> : <UnfoldMore fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!hasContent ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            No items selected
          </Typography>
        </Box>
      ) : (
        <>
          {/* Demographics */}
          {hasDemographics && (
            <PreviewSection
              sectionKey="demographics"
              label="Demographics"
              isActive={isSectionActive('demographics')}
              summary={demographicsSummary}
              onClickCollapsed={() => onSectionChange('demographics')}
              setRef={setSectionRef}
              showDivider={hasCoverages || hasModules}
            >
              <List dense disablePadding>
                {resolvedDemographics.map(d => (
                  <ListItem key={d.label} sx={{ py: 0.25, px: 2 }}>
                    <ListItemText
                      primary={`${d.label}: ${d.value}`}
                     slotProps={{ primary: { variant: 'body2' } }}/>
                  </ListItem>
                ))}
              </List>
            </PreviewSection>
          )}

          {/* Coverages */}
          {hasCoverages && (
            <PreviewSection
              sectionKey="coverages"
              label="Coverages"
              isActive={isSectionActive('coverages')}
              summary={coverageSummary}
              onClickCollapsed={() => onSectionChange('coverages')}
              setRef={setSectionRef}
              showDivider={hasModules}
            >
              <List dense disablePadding>
                {resolvedCoverages.map(c => (
                  <ListItem key={c.label} sx={{ py: 0.25, px: 2 }}>
                    <ListItemText
                      primary={`${c.label}: ${c.value}`}
                     slotProps={{ primary: { variant: 'body2' } }}/>
                  </ListItem>
                ))}
              </List>
            </PreviewSection>
          )}

          {/* Modules */}
          {moduleEntries.map(({ key, mod, selectedItems }, idx) => {
            const firstLabel = selectedItems[0]?.displayLabel ?? '';
            const moduleSummary = selectedItems.length === 1
              ? firstLabel
              : `${firstLabel}${selectedItems.length > 1 ? `, +${selectedItems.length - 1} more` : ''}`;

            return (
              <PreviewSection
                key={key}
                sectionKey={`module:${key}`}
                label={mod.name}
                chipCount={selectedItems.length}
                isActive={isSectionActive(`module:${key}`)}
                summary={moduleSummary}
                onClickCollapsed={() => onSectionChange(`module:${key}`)}
                setRef={setSectionRef}
                showDivider={idx < moduleEntries.length - 1}
              >
                <List dense disablePadding>
                  {selectedItems.map(item => (
                    <ListItem key={item.selectionKey} sx={{ py: 0.25, px: 2 }}>
                      <ListItemText
                        primary={item.displayLabel}
                        secondary={item.timestamp ? formatDate(item.timestamp) : undefined}
                       slotProps={{ primary: { variant: 'body2', noWrap: true }, secondary: { variant: 'caption' } }}/>
                    </ListItem>
                  ))}
                </List>
              </PreviewSection>
            );
          })}
        </>
      )}
    </Paper>
  );
}

// ---------------------------------------------------------------------------
// Preview section with progressive reveal
// ---------------------------------------------------------------------------

interface PreviewSectionProps {
  sectionKey: string;
  label: string;
  chipCount?: number;
  isActive: boolean;
  summary: string;
  onClickCollapsed: () => void;
  setRef: (key: string, el: HTMLDivElement | null) => void;
  showDivider: boolean;
  children: React.ReactNode;
}

function PreviewSection({
  sectionKey,
  label,
  chipCount,
  isActive,
  summary,
  onClickCollapsed,
  setRef,
  showDivider,
  children,
}: PreviewSectionProps) {
  const refCallback = useCallback(
    (el: HTMLDivElement | null) => setRef(sectionKey, el),
    [sectionKey, setRef],
  );

  if (!isActive) {
    // Collapsed summary — clickable to expand
    return (
      <>
        <ButtonBase
          onClick={onClickCollapsed}
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            textAlign: 'left',
            px: 2,
            py: 0.75,
            gap: 1,
            '&:hover': { backgroundColor: 'grey.50' },
          }}
        >
          <Typography variant="body2" noWrap sx={{fontWeight: 600,  minWidth: 0, flexShrink: 1 }}>
            {label}
          </Typography>
          {chipCount != null && (
            <Chip
              label={chipCount}
              size="small"
              sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }}
            />
          )}
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0 }}
          >
            {summary}
          </Typography>
        </ButtonBase>
        {showDivider && <Divider />}
      </>
    );
  }

  // Expanded active section
  return (
    <Box ref={refCallback}>
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          backgroundColor: 'grey.50',
          borderLeft: '3px solid',
          borderLeftColor: 'primary.main',
        }}
      >
        <Typography variant="body2" noWrap sx={{fontWeight: 600,  minWidth: 0, flex: 1 }}>
          {label}
        </Typography>
        {chipCount != null && (
          <Chip
            label={chipCount}
            size="small"
            sx={{ height: 20, fontSize: '0.75rem' }}
          />
        )}
      </Box>
      <Box sx={{ borderLeft: '3px solid', borderLeftColor: 'primary.main' }}>
        {children}
      </Box>
      {showDivider && <Divider />}
    </Box>
  );
}
